import _ from 'lodash'
import {
	ClassicOnlyVariant,
	InnerVariant,
	OuterVariant,
	TransformData,
	TransitionData,
	VARIANTS_CSS_STRATEGIES,
} from '@wix/thunderbolt-becky-types'

import postcss from 'postcss'
import nestedPostCssPlugin from 'postcss-nested'
// @ts-ignore
import discardEmptyRulesPostCssPlugin from 'postcss-discard-empty'

import { compIdToCssSelector, getDisplayedId, getItemId, isDisplayedOnly } from '@wix/thunderbolt-commons'
import { getCSSFromTransitionItem } from './transitionFunctionLibrary'
import { getCSSFromTransformationItem } from './transformationsFunctionLibrary'
import { CssAndBreakpointId } from '../carmi/variants/types'

type VariantCssOptions = {
	componentDefaultRotation?: number
	childrenSelectorStrategy?: string
	dataType: 'ComponentStyle' | 'TransformData' | 'TransitionData'
	isRepeaterChild?: boolean
	shouldAddIdSelector?: boolean
	isHoverBoxChild?: boolean
	ignoredTransformProps?: Array<string>
	isEditor?: boolean
}

export type GetCssWithVariantsParams = {
	cssValue: string
	variants?: Array<ClassicOnlyVariant>
	compId: string
	options: VariantCssOptions
}

const innerConditionTypes = ['Hover', 'Class']
const notScreenInAnimationSelector = ':not(.animating-screenIn)'

const getRegularIdSelector = (compId: string) => `#${compId}`
const getTemplateRepeaterIdSelector = (compId: string) => `[id^="${compId}__"]`
const getInflatedRepeaterIdSelector = (compId: string) => `[id="${compId}"]`

const breakpointIdToCss = (cssAndBreakpointIdArray: Array<CssAndBreakpointId>) => {
	return cssAndBreakpointIdArray.reduce((acc: Record<string, string>, val) => {
		if (acc[val.breakpointId]) {
			acc[val.breakpointId] += `\n${val.css}`
		} else {
			acc[val.breakpointId] = val.css
		}

		return acc
	}, {})
}

const getHoverChildSelector = (compId: string) => {
	return compIdToCssSelector(compId, false)
}

const getIdSelector = (
	compId: string,
	isTemplateChildOfRepeater: boolean = false,
	shouldAddIdSelector: boolean = true,
	isHoverBoxChild: boolean = false
) => {
	if (!shouldAddIdSelector) {
		return ''
	}

	if (isHoverBoxChild) {
		return getHoverChildSelector(compId)
	}

	// TB-2804
	// We get both inflated and template child of repeater.
	// We need them to have the same "strength", so we use id=displayedId instead of #displayedId
	// for both compSpecificCss and EffectStyle.
	const isInflatedChildOfRepeater = isDisplayedOnly(compId)
	if (isInflatedChildOfRepeater) {
		return getInflatedRepeaterIdSelector(compId)
	}

	if (isTemplateChildOfRepeater) {
		return getTemplateRepeaterIdSelector(compId)
	}

	return getRegularIdSelector(compId)
}

const getSelectorByVariant = (variant: ClassicOnlyVariant) => {
	switch (variant.type) {
		case 'Hover':
			return ':hover'
		case 'Preset':
		case 'Class':
			return `.${variant.id}`
		case 'Mobile':
			return '.device-mobile-optimized'
		default:
			// no matching type, throwing
			// @ts-ignore
			throw new Error(`Variant condition does not exist. type: ${variant.type}`)
	}
}

const fixDisplayedOnlyCompId = (compIdToFix: string, containerId: string) => {
	const itemId = getItemId(containerId)
	if (itemId) {
		return getDisplayedId(compIdToFix, itemId)
	}
	return compIdToFix
}

const handleTransitionStrategy = (selector: string, options: VariantCssOptions) => {
	if (options.dataType === 'TransitionData') {
		const isAllChildren = options.childrenSelectorStrategy === VARIANTS_CSS_STRATEGIES.ALL_CHILDREN
		return isAllChildren ? `${selector}, ${selector} * ` : `${selector}, ${selector} > *`
	}
	return selector
}

const getCssValueByDataType = (itemWithConditions: TransformData | TransitionData, options: VariantCssOptions) => {
	switch (options.dataType) {
		case 'TransformData':
			return getCSSFromTransformationItem(
				itemWithConditions as TransformData,
				options.ignoredTransformProps!,
				options.componentDefaultRotation
			)
		case 'TransitionData':
			return getCSSFromTransitionItem(itemWithConditions as TransitionData)
		default:
			return ''
	}
}

const hoverSelectorRegex = new RegExp(':hover', 'g')

const addA11yRules = (selector: string) => {
	const focusRule = selector.replace(hoverSelectorRegex, ':focus-within')
	return `${selector},.keyboard-tabbing-on ${focusRule}`
}

const addClassRule = (selector: string, variantId: string) => {
	const focusRule = selector.replace(hoverSelectorRegex, `.${variantId}`)
	return `${selector}, ${focusRule}`
}

const computeHoverLogic = (
	selector: string,
	targetIdToVariants: Record<string, Array<InnerVariant>>,
	currentTargetId: string,
	isEditor?: boolean
) => {
	const hoverVariant = targetIdToVariants[currentTargetId].find((v) => v.type === 'Hover')

	if (hoverVariant) {
		// isEditor = true means we are NOT in preview nor public
		if (isEditor) {
			return addClassRule(selector, hoverVariant.id)
		} else {
			return addA11yRules(selector)
		}
	}
	return selector
}

const getCssWithInnerVariants = (
	targetIdToVariants: Record<string, Array<InnerVariant>>,
	orderedTargetIds: Array<string>,
	cssValue: string,
	compId: string,
	options: VariantCssOptions
) => {
	// this recursion creates scss string which we then is used to generate regular css
	const getCssRec = (targetIds: Array<string>): string => {
		if (!targetIds.length) {
			return cssValue
		}
		const currentTargetId = targetIds.shift()!

		const pseudoSelector = targetIdToVariants[currentTargetId]
			.map((variant) => getSelectorByVariant(variant))
			.join('')

		const fixedCompId = fixDisplayedOnlyCompId(currentTargetId, compId)
		let selector = `${getIdSelector(fixedCompId, options.isRepeaterChild)}${pseudoSelector}`

		if (!targetIds.length) {
			// if none of the condition include the comp id we need to select it using #compId
			if (fixDisplayedOnlyCompId(currentTargetId, compId) !== compId) {
				selector = `${selector} ${getIdSelector(compId, options.isRepeaterChild)}`
			}
			// if we are in Transition style we need to select all children or only direct children to be affected.
			if (options.dataType === 'TransitionData') {
				selector = handleTransitionStrategy(selector, options)
			}

			return `${computeHoverLogic(
				selector,
				targetIdToVariants,
				currentTargetId,
				options.isEditor
			)} { ${cssValue} }`
		}

		return `${computeHoverLogic(selector, targetIdToVariants, currentTargetId, options.isEditor)} { ${getCssRec(
			targetIds
		)} }`
	}

	return getCssRec(orderedTargetIds)
}

const getCssWithOuterVariants = (outerConditions: Array<OuterVariant>, innerVariantsCss: string) => {
	const outerCondition = outerConditions[0] // Can it be multiple conditions?
	const outerSelector = getSelectorByVariant(outerCondition)
	return `${outerSelector} { ${innerVariantsCss} }`
}

const generateCss = (nestedCss: string): string =>
	postcss([nestedPostCssPlugin, discardEmptyRulesPostCssPlugin]).process(nestedCss, { map: false, from: undefined })
		.css

const splitConditions = (variants: Array<ClassicOnlyVariant>): [Array<InnerVariant>, Array<OuterVariant>] =>
	// @ts-ignore
	_.partition(variants, (variant) => innerConditionTypes.includes(variant.type))

const getTargetIds = (innerConditions: Array<InnerVariant>) =>
	_.chain(innerConditions)
		.map((variant) => variant.componentId)
		.uniq()
		.value()

const getInnerConditionsGroupByTarget = (innerConditions: Array<InnerVariant>) =>
	innerConditions.reduce((targetIdToVariants, variant) => {
		targetIdToVariants[variant.componentId] = [...(targetIdToVariants[variant.componentId] || []), variant]
		return targetIdToVariants
	}, {} as Record<string, Array<InnerVariant>>)

const getDefaultCss = (cssValue: string, compId: string, options: VariantCssOptions) => {
	let selector

	selector = getIdSelector(compId, options.isRepeaterChild, options.shouldAddIdSelector, options.isHoverBoxChild)

	// We disable default interactions (and not componentStyle) when there is active screen-in animation
	const isInteraction = options.dataType !== 'ComponentStyle'
	selector = isInteraction ? `${selector}${notScreenInAnimationSelector}` : selector

	if (options.dataType === 'TransitionData') {
		selector = handleTransitionStrategy(selector, options)
	}

	const defaultCss = `${selector} { ${cssValue} }`
	try {
		return generateCss(defaultCss)
	} catch {
		// TODO: report error in css (#TB-1988)
		return defaultCss
	}
}

const getCssUnderVariants = (
	variants: Array<ClassicOnlyVariant>,
	cssValue: string,
	compId: string,
	options: VariantCssOptions
) => {
	const [innerConditions, outerConditions] = splitConditions(variants)
	const targetIds: Array<string> = getTargetIds(innerConditions)
	// this grouping is needed in order to combine multiple condition on same target (#container:hover.class)
	const groupedInnerVariants = getInnerConditionsGroupByTarget(innerConditions)

	const innerVariantsCss = innerConditions.length
		? getCssWithInnerVariants(groupedInnerVariants, targetIds, cssValue, compId, options)
		: `${getIdSelector(compId, options.isRepeaterChild)} { ${cssValue} }` // Only outer variants, need to select to compId

	const variantsCss = outerConditions.length
		? getCssWithOuterVariants(outerConditions, innerVariantsCss)
		: innerVariantsCss

	return generateCss(variantsCss)
}

const getCssWithVariants = ({ cssValue, variants, compId, options }: GetCssWithVariantsParams) => {
	if (variants?.length) {
		return getCssUnderVariants(variants, cssValue, compId, options)
	} else {
		return getDefaultCss(cssValue, compId, options)
	}
}

export { getCssWithVariants, getCssValueByDataType, breakpointIdToCss }
